import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Typography,
  TextField,
  Button,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
  IconButton,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import {
  listPlatformPassengerTaxProfiles,
  savePlatformPassengerTaxProfile,
  deletePlatformPassengerTaxProfile,
  postPlatformPassengerFeesBackfill,
} from "../../../api/passagensApi";
import { PASSENGER_PAYMENT_RAIL_OPTIONS, paymentRailLabel, fmtPctDisplay } from "./passengerTaxConstants";

const EMPTY = {
  payment_rail: "*",
  taxa_contratual_pct: 0,
  sobretaxa_pct: 0,
};

export default function PassengerTaxProfilesPanel({ dominioId, from, to, onProfilesSaved }) {
  const { t } = useTranslation("app");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!dominioId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listPlatformPassengerTaxProfiles(dominioId);
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("platformPage.passengerContractFeesLoadError"));
    } finally {
      setLoading(false);
    }
  }, [dominioId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const triggerFeesRecalc = useCallback(async () => {
    if (!dominioId || !from || !to) {
      onProfilesSaved?.();
      return;
    }
    try {
      let offsets = {};
      for (let i = 0; i < 80; i += 1) {
        const data = await postPlatformPassengerFeesBackfill({
          from,
          to,
          dominioId,
          offsets,
          forceMpBackfill: true,
        });
        offsets = data?.offsets && typeof data.offsets === "object" ? data.offsets : {};
        if (!data?.hasMore) break;
      }
    } catch {
      /* best effort */
    }
    onProfilesSaved?.();
  }, [dominioId, from, onProfilesSaved, to]);

  const handleSave = async () => {
    setError("");
    try {
      await savePlatformPassengerTaxProfile(dominioId, form);
      setForm(EMPTY);
      await load();
      await triggerFeesRecalc();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("platformPage.passengerContractFeesSaveError"));
    }
  };

  const handleDelete = async (id) => {
    setError("");
    try {
      await deletePlatformPassengerTaxProfile(dominioId, id);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t("platformPage.passengerContractFeesSaveError"));
    }
  };

  if (!dominioId) {
    return (
      <Alert severity="info" sx={{ py: 0.75 }}>
        {t("platformPage.passengerContractFeesSelectWorkspace")}
      </Alert>
    );
  }

  if (loading) return <CircularProgress size={24} />;

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Alert severity="info" sx={{ py: 0.75 }}>
        {t("platformPage.passengerContractFeesHint")}
      </Alert>
      <Typography variant="subtitle2" fontWeight={600}>
        {t("platformPage.passengerContractFeesTitle")}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          select
          size="small"
          sx={{ minWidth: 240 }}
          label={t("platformPage.passengerTaxColPaymentRail")}
          value={form.payment_rail}
          onChange={(e) => setForm({ ...form, payment_rail: e.target.value })}
        >
          {PASSENGER_PAYMENT_RAIL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          sx={{ width: 140 }}
          label={t("platformPage.passengerTaxColContractualPct")}
          value={form.taxa_contratual_pct}
          onChange={(e) => setForm({ ...form, taxa_contratual_pct: e.target.value })}
        />
        <TextField
          size="small"
          type="number"
          sx={{ width: 120 }}
          label={t("platformPage.passengerTaxColSurchargePct")}
          value={form.sobretaxa_pct}
          onChange={(e) => setForm({ ...form, sobretaxa_pct: e.target.value })}
        />
        <Button variant="contained" onClick={handleSave}>
          {t("common.save", { defaultValue: "Salvar" })}
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("platformPage.passengerTaxColPaymentRail")}</TableCell>
              <TableCell align="right">{t("platformPage.passengerTaxColContractualPct")}</TableCell>
              <TableCell align="right">{t("platformPage.passengerTaxColSurchargePct")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t("platformPage.passengerContractFeesEmpty")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{paymentRailLabel(r.payment_rail)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtPctDisplay(
                      r.taxa_contratual_pct != null && Number(r.taxa_contratual_pct) > 0
                        ? r.taxa_contratual_pct
                        : r.taxa_xvision_pct
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {fmtPctDisplay(r.sobretaxa_pct)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleDelete(r.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
